import _ from "lodash";
import React, { useState, useCallback } from "react";
import classes from "./UserMenuButton.scss";
import { TwistyIcon } from "SvgComponents";
import I18nHOC from "UIComponents/I18nHOC";
import { Avatar, Dropdown, DropdownMenu, Button } from "@toddle-edu/ds-web";
import {
  SettingsOutlined,
  CommentDotsLeftOutlined,
  LanguageOutlined,
  LogoutOutlined,
  UserProfileOutlined,
  SchoolOutlined,
  SetupOutlined,
  DashboardOutlined,
  StarsTwoFilled,
  SwitchArrowsOutlined,
} from "@toddle-edu/ds-icons";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";
import { openLinkInNewTab } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import CoachMarks from "AppComponents/CoachMarks";
import LogoutModal from "AppComponents/UserMenuButton/components/LogoutModal";
import { getLanguageSelectorOptions, loadLocalesBundle } from "Utils";
import client from "lib/apolloClient/client";
import { IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP } from "Constants/permissionConstants";
import { getEnvironment, getGraphQlPlaygroundUrl } from "Utils/UrlHelpers";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import {
  getUserInfo,
  closeSentralPortalIframe,
} from "Login/modules/LoginModule/LoginUtils";

import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
  PLATFORM_ACCESS_MANAGER,
  TOOLS,
  GRAPHQL_PLAYGROUND,
  INTERNAL_PORTAL_ACCESS,
  INTERNAL_PORTAL,
  MOBILE_MEDIA_QUERY,
} from "Constants/stringConstants";
import { updateCoachMarkStatus } from "modules/CoachMarksModule";
import { getStudentCoachMarksFromCache } from "modules/CommonGraphqlHelpers";
import classNames from "classnames";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getCanAccessProfileV2 } from "Platform/routes/ProfileV2/modules/Utils";
import { canOpenTeacherProfile } from "Platform/routes/TeacherProfile/modules/Utils";
import { navigateToMyFamilyProfile } from "modules/NavigationModule";
import { canParentAccessTheirProfile } from "FamilyProfile/modules/Utils";
import { getIsChildBlocked } from "modules/CommonSelectors";
import GPTVision from "AppComponents/GPTPlayground/components/GPTVision";
import GPTPlayground from "AppComponents/GPTPlayground";
import LanguageTimezoneModal from "AppComponents/LanguageTimezoneModal";
import useMediaQuery from "Hooks/UseMediaQuery";
import { isSentralPortalFeatureEnabled } from "Utils/ACLStoreUtils";
import { SENTRAL_PERMISSION_CONFIGURATIONS } from "Login/components/SentralPortalModal/SentralPortalUtils";

const LANGUAGE_SWITCHER_IN_CURRICULUM = [
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
];

// list down all the coachmarks that should be checked
// if any of them is not viewed, the red dot will appear near the user name
// in the user menu
const ALLOWED_COACHMARKS = [
  {
    coachMark: "SHARE_FEEDBACK_BUTTON",
    permission: "FeatureFlag:FeedbackForm",
  },
];

const COACHMARKS_LANGUAGE_SWITCHER_BUTTON = {
  target: `#COACHMARKS_LANGUAGE_SWITCHER_BUTTON`,
};
const getNonViewedCoachMark = coachMarks => {
  return _.find(coachMarks, each => {
    const findCoachMarkFromAllowedList = _.find(
      ALLOWED_COACHMARKS,
      requiredCoachMark =>
        requiredCoachMark.coachMark === each.coachMark &&
        (requiredCoachMark.permission
          ? ACLStore.can(requiredCoachMark.permission)
          : true)
    );

    return findCoachMarkFromAllowedList && _.get(each, "isViewed") === false;
  });
};

const UserMenu = ({ user = {}, coachMarks }) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const {
    profile_image,
    first_name = "",
    last_name = "",
    middle_name = "",
    preferred_name = "",
    prefix = "",
    suffix = "",
    pronouns = "",
    user_type,
  } = user;

  const userMenuBeaconStyles = classNames({
    [classes.beacon]: true,
    [classes.userMenuBeacon]: true,
  });

  const userName = getFormattedUserName({
    user: {
      firstName: first_name,
      middleName: middle_name,
      lastName: last_name,
      preferredName: preferred_name,
      prefix,
      suffix,
      pronouns,
      type: user_type,
    },
  });

  return (
    <div
      className={classes.container}
      id={"COACHMARKS_LANGUAGE_SWITCHER_BUTTON"}
    >
      <Avatar
        name={userName}
        color="blue"
        size={isMobile ? "small" : "x-small"}
        src={profile_image}
      />
      {getNonViewedCoachMark(coachMarks) ? (
        <div className={userMenuBeaconStyles}></div>
      ) : null}
      <div className={classes.username}>{userName}</div>
      <div className={classes.bottomArrow}>
        <TwistyIcon />
      </div>
    </div>
  );
};
let buttonDropDownRef = null;

const UserMenuButton = ({
  user,
  onLogout,
  onSwitchToAdminClick,
  onSwitchToPlatformAccessManagerClick,
  onSwitchToToolsV2,
  onSwitchToToolsClick,
  onStudentFeedbackClick,
  onProfileSettingsClick,
  t,
  isAdminMenu,
  onTeacherPortalClick,
  updateSettings,
  userId,
  i18n,
  currentCurriculumProgramType,
  loginActiveTab,
  coachMarks,
  portalType,
  updateCoachMarkStatus,
  isOnboarding = false,
  showStudentFeedback = false,
  isStudentProfileV2,
  isTeacherProfileEnabled,
  setActiveHomepageCard,
  navigateToMyFamilyProfile,
  openLinkInNewTab,
  isChildBlocked,
  verifyUserSession,
  showTimezoneSwitcher,
}) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [aiModalType, setAIModalType] = useState("");
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [showLanguageTimezoneModal, setShowLanguageTimezoneModal] = useState(
    false
  );

  const userType = _.get(user, "user_type");

  const isLanguageSwitcherVisibleInCurrentCurriculum = _.includes(
    LANGUAGE_SWITCHER_IN_CURRICULUM,
    currentCurriculumProgramType
  );

  const showStudentFeedbackButton =
    showStudentFeedback && ACLStore.can("FeatureFlag:FeedbackForm");

  const changeLocalesLocally = async value => {
    const settings = [{ name: "locale", value }];

    // when user changes the platform language then update the AI voice language too
    if (userType === "staff") {
      const languageId = _.toString(
        _.find(
          AI_TRANSLATION_LANGUAGES,
          language => _.get(language, "code") === value
        )?.id
      );

      if (languageId) {
        settings.push(
          {
            name: "ConfigureProgressReportAIVoiceLanguage",
            value: languageId,
          },
          {
            name: "ConfigureAssessmentAIVoiceLanguage",
            value: languageId,
          },
          {
            name: "ConfigureRTEAIVoiceLanguage",
            value: languageId,
          }
        );
      }
    }

    updateSettings({ settings });

    await loadLocalesBundle(value);
    i18n.changeLanguage(value);
    if (ACLStore.can("FeatureFlag:RefetchQueriesOnLanguageChange")) {
      setTimeout(() => {
        client.reFetchObservableQueries(true);
      });
    }
  };

  const onDropdownOptionClick = e => {
    e.domEvent?.stopPropagation();
    const action = e.key;
    const keyPath = e.keyPath;
    onItemClick(action, keyPath);
  };

  const showLanguageSwitcher =
    ((ACLStore.can("TeacherPortal:ChangeLanguage") && userType === "staff") ||
      (ACLStore.can("Common:ChangeLanguage") &&
        (userType === "student" || userType === "parent"))) &&
    (isLanguageSwitcherVisibleInCurrentCurriculum || loginActiveTab == "admin");

  const coachMarkForStudentFeedback = _.find(coachMarks, {
    coachMark: "SHARE_FEEDBACK_BUTTON",
  });

  const makeShareFeedbackCoachMarkSeen = () => {
    if (_.get(coachMarkForStudentFeedback, "isViewed") === false) {
      updateCoachMarkStatus({
        portalType,
        type: "SHARE_FEEDBACK_BUTTON",
        coachMarkId: _.get(coachMarkForStudentFeedback, "id"),
      });
    }
  };

  const dropdownOptionBeaconStyles = classNames({
    [classes.dropdownMenuOptionBeacon]: true,
    [classes.beacon]: true,
  });

  let profileSettingsLabel = t("common:profile_setting");
  let profileSettingsIcon = (
    <SettingsOutlined variant={"subtle"} size="xx-small" />
  );

  if (userType === "student" && isStudentProfileV2) {
    profileSettingsLabel = t("common:my_with_label", {
      label: t("common:profile"),
    });
    profileSettingsIcon = (
      <UserProfileOutlined variant={"subtle"} size="xx-small" />
    );
  } else if (userType === "staff" && isTeacherProfileEnabled) {
    profileSettingsLabel = t("common:my_with_label", {
      label: t("common:profile"),
    });
    profileSettingsIcon = (
      <UserProfileOutlined variant={"subtle"} size="xx-small" />
    );
  } else if (userType === "parent") {
    profileSettingsLabel = t("configuration:notifications_preferences");
  }

  const isPlatformAccessManagerEnabled = ACLStore.can(
    _.get(IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP, PLATFORM_ACCESS_MANAGER)
  );
  const isToolsV2Enabled = ACLStore.can(
    _.get(IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP, TOOLS)
  );

  const isInternalPortalEnabled =
    ACLStore.can(
      _.get(IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP, INTERNAL_PORTAL_ACCESS)
    ) &&
    ACLStore.can(
      _.get(IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP, INTERNAL_PORTAL)
    );

  const isFamilyProfile = canParentAccessTheirProfile({ userType });

  const isGraphQlPlaygroundEnabled =
    _.includes(["staging", "development"], getEnvironment()) &&
    ACLStore.can(
      _.get(IDENTITY_CONFIGURATION_TYPE_TO_PERM_MAP, GRAPHQL_PLAYGROUND)
    );

  const isAIPlaygroundEnabled = ACLStore.can("FeatureFlag:AIPlayground");

  const isAdmissionsPOCEnabled =
    userType === "parent" && ACLStore.can("FeatureFlag:AdmissionsPOC");

  const isSentralProfileEnabled = isSentralPortalFeatureEnabled(
    SENTRAL_PERMISSION_CONFIGURATIONS.STUDENT_PROFILE
  );

  const MENU_ITEMS = [
    !isOnboarding &&
    !isChildBlocked && // If the child is blocked then hide the setting button for parent
    !(userType === "student" && isSentralProfileEnabled) // if sentral profile is enabled then hide the profile button for student
      ? {
          label: profileSettingsLabel,
          icon: profileSettingsIcon,
          key: "profile_settings",
        }
      : null,
    isAIPlaygroundEnabled
      ? {
          label: "AI Playground",
          icon: <StarsTwoFilled variant={"subtle"} size="xx-small" />,
          key: "ai_playground",
        }
      : null,
    isFamilyProfile && !isSentralProfileEnabled // if sentral profile is enabled then hide the profile button for parent
      ? {
          label: t("common:my_with_label", {
            label: t("common:profile"),
          }),
          icon: <UserProfileOutlined variant={"subtle"} size="xx-small" />,
          key: "family_profile",
        }
      : null,
    showLanguageSwitcher
      ? showTimezoneSwitcher
        ? {
            label: t("common:language_and_timezone"),
            icon: <LanguageOutlined variant={"subtle"} size="xx-small" />,
            key: "language_and_timezone_settings",
          }
        : {
            label: t("common:language"),
            icon: <LanguageOutlined variant={"subtle"} size="xx-small" />,
            key: "language_settings",
            options: getLanguageSelectorOptions(),
            isSubMenu: true,
          }
      : null,
    showStudentFeedbackButton
      ? {
          label: t("common:share_feedback"),
          icon: (
            <span style={{ display: "flex", alignItems: "center" }}>
              {coachMarkForStudentFeedback &&
              !_.get(coachMarkForStudentFeedback, "isViewed") ? (
                <div className={dropdownOptionBeaconStyles}></div>
              ) : null}
              <CommentDotsLeftOutlined variant={"subtle"} size="xx-small" />
            </span>
          ),
          key: "student_feedback",
          customTestId: "userMenuButton-dropdownMenu-shareFeedback-button",
        }
      : null,
    // getIsAdminByRole() && !isOrgFree && !isAdminMenu
    //   ? {
    //       label: t("teacherHomePage:switch_to_admin"),
    //       svg: <SwitchToAdminIcon width={16} height={16} />,
    //       value: "switch_to_admin"
    //     }
    //   : null,
    // ACLStore.can("TeacherPortal") && isAdminMenu
    //   ? {
    //       label: t("teacherHomePage:switch_to_teacher"),
    //       svg: <SwitchToAdminIcon width={16} height={16} />,
    //       value: "switch_to_teacher"
    //     }
    //   : null,
    !isInternalPortalEnabled && isPlatformAccessManagerEnabled
      ? {
          label: "Platform Access Manager",
          icon: <SchoolOutlined variant={"subtle"} size="xx-small" />,
          key: "platform_access_manager",
        }
      : null,
    isInternalPortalEnabled
      ? {
          label: "Internal Portal",
          icon: <SchoolOutlined variant={"subtle"} size="xx-small" />,
          key: "internal_portal",
        }
      : null,
    isGraphQlPlaygroundEnabled
      ? {
          label: "GraphQL Playground",
          icon: <DashboardOutlined variant={"subtle"} size="xx-small" />,
          key: "graphql_playground",
        }
      : null,
    !isInternalPortalEnabled && isToolsV2Enabled
      ? {
          label: "Switch to Tools",
          icon: <SetupOutlined variant={"subtle"} size="xx-small" />,
          key: "switch_to_tool_v2",
        }
      : null,
    isAdmissionsPOCEnabled
      ? {
          label: "Switch to Admission",
          icon: <SwitchArrowsOutlined variant={"subtle"} size="xx-small" />,
          key: "switch_to_admission",
        }
      : null,
    {
      label: t("common:logout"),
      icon: <LogoutOutlined variant={"subtle"} size="xx-small" />,
      key: "logout",
    },
  ];

  const onItemClick = (value, keyPath) => {
    if (_.size(keyPath) > 1) {
      switch (keyPath[1]) {
        case "language_settings": {
          changeLocalesLocally(value);
          break;
        }
      }
    }
    switch (value) {
      case "profile_settings": {
        onProfileSettingsClick();
        break;
      }
      case "ai_playground": {
        setAIModalType("GPT_PLAYGROUND");
        break;
      }
      case "language_and_timezone_settings": {
        setShowLanguageTimezoneModal(true);
        break;
      }
      // case "switch_to_admin": {
      //   onSwitchToAdminClick();
      //   break;
      // }
      case "switch_to_tool": {
        onSwitchToToolsClick();
        break;
      }
      case "family_profile": {
        navigateToMyFamilyProfile();
        break;
      }
      case "platform_access_manager": {
        verifyUserSession("PLATFORM_ACCESS_MANAGER");
        break;
      }
      case "internal_portal": {
        verifyUserSession("INTERNAL_PORTAL");
        break;
      }
      case "switch_to_tool_v2": {
        verifyUserSession("SWITCH_TO_TOOL_V2");
        break;
      }
      case "switch_to_admission": {
        // POC: cross-subdomain SSO via parent-domain cookies. Update this URL
        // to the Netlify preview / staging-admissions DNS when ready.
        openLinkInNewTab({
          url: "https://staging-admissions.toddleapp.com",
        });
        break;
      }
      case "student_feedback": {
        onStudentFeedbackClick();
        makeShareFeedbackCoachMarkSeen();
        break;
      }
      case "logout": {
        setLogoutModalOpen(true);
        break;
      }
      case "graphql_playground": {
        openLinkInNewTab({
          url: getGraphQlPlaygroundUrl({ jwt: _.get(getUserInfo(), "jwt") }),
        });
        break;
      }
      // case "switch_to_teacher": {
      //   onTeacherPortalClick();
      //   break;
      // }
    }
  };
  const updateButtonDropDownRef = ref => {
    buttonDropDownRef = ref;
  };

  const onModalClose = () => {
    setAIModalType("");
  };

  const onCoachmarkStepChanged = useCallback(({ isDone }) => {
    if (isDone && buttonDropDownRef) {
      buttonDropDownRef.updateShowDropdown(true);
    }
  }, []);

  const handleLogout = async ({ logoutAllDevices }) => {
    await onLogout({ logoutAllDevices });
    setLogoutModalOpen(false);
  };

  const onTimezoneModalClose = () => {
    setShowLanguageTimezoneModal(false);
  };

  return (
    <React.Fragment>
      {showLanguageSwitcher && (
        <CoachMarks
          type={`LANGUAGE_SWITCHER_BUTTON`}
          continuous={false}
          params={COACHMARKS_LANGUAGE_SWITCHER_BUTTON}
          onStepChanged={onCoachmarkStepChanged}
        />
      )}
      <Dropdown
        getPopupContainer={el => {
          return el;
        }}
        overlay={
          <DropdownMenu
            onClick={onDropdownOptionClick}
            options={_.filter(MENU_ITEMS, item => !!item)}
          />
        }
        className={classes.dropdown}
        placement={isMobile ? "bottom" : "bottomRight"}
        onVisibleChange={closeSentralPortalIframe}
      >
        <Button
          variant={"plain"}
          className={classes.menuBtn}
          testId="userMenuButton-userMenu-open"
        >
          <UserMenu user={user} coachMarks={coachMarks} />
        </Button>
      </Dropdown>
      {aiModalType === "GPT_VISION" && (
        <GPTVision onModalClose={onModalClose} />
      )}
      {aiModalType === "GPT_PLAYGROUND" && (
        <GPTPlayground onModalClose={onModalClose} />
      )}

      <LogoutModal
        t={t}
        logoutModalOpen={logoutModalOpen}
        setLogoutModalOpen={setLogoutModalOpen}
        handleLogout={handleLogout}
      />
      {showLanguageTimezoneModal && (
        <LanguageTimezoneModal onModalClose={onTimezoneModalClose} />
      )}
    </React.Fragment>
  );
};

const UserMenuButtonMemoized = React.memo(UserMenuButton);

const BLANK_ARRAY = [];
const mapStateToProps = (state, ownProps) => {
  const isCommunity = _.get(state, "login.activeTab") == "community";
  const user = state.login.userInfo;
  const userId = _.get(user, "id");

  const userType = _.get(user, "user_type");

  const isStudentProfileV2 = getCanAccessProfileV2({ userType });
  const isTeacherProfileEnabled = canOpenTeacherProfile({ userType });

  const isChildBlocked = getIsChildBlocked();

  const coachMarks =
    userType === "student"
      ? _.get(
          getStudentCoachMarksFromCache(userId),
          "coachMarkStatus",
          BLANK_ARRAY
        )
      : BLANK_ARRAY;

  const showTimezoneSwitcher = ACLStore.can("FeatureFlag:GlobalTimezone");

  return {
    user,
    userId,
    portalType: isCommunity ? "COMMUNITY" : "PLANNER",
    coachMarks,
    loginActiveTab: state.login.activeTab,
    currentCurriculumProgramType: _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    ),
    isStudentProfileV2,
    isTeacherProfileEnabled,
    isChildBlocked,
    showTimezoneSwitcher,
  };
};

const mapActionCreators = {
  updateSettings,
  updateCoachMarkStatus,
  navigateToMyFamilyProfile,
  openLinkInNewTab,
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage"] }),
  connect(mapStateToProps, mapActionCreators)
)(UserMenuButtonMemoized);
